import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-use';
import { merge } from 'lodash';
import _compact from 'lodash/compact';

import { useTranslation } from '@lyearn/i18n';
import { Breadcrumb, useBreadcrumbContext } from '@/components/Breadcrumb/context';
import { BREADCRUMB_QUERY_PARAM } from '@/components/Breadcrumb/hooks/useBreadcrumb';
import { FilesFilters } from '@/types/schema';

import { DefaultCategoryId } from '../../Discover/components/CategorySidebar/conts';
import { CATEGORY_ID_QUERY_PARAM } from '../../Discover/components/CategorySidebar/utils';
import { CategoryFileFragment } from '../graphql/fragment/generated/CategoryFile';
import { useCategoriesQuery } from '../graphql/queries/generated/categories';
import { CategoryType } from '../types';
import { buildCategoriesMap } from '../utils';

export type UseCategoriesProps = Pick<FilesFilters, 'crossSubWorkspace' | 'subWorkspaceIds'>;

function buildCategoryTree(
  categories: CategoryType[],
  categoriesMap: { [id: string]: CategoryFileFragment },
): CategoryType[] {
  return _compact(
    categories.map((category) => {
      const children = _compact(
        category?.childrenIds?.map((id) => categoriesMap?.[id]) || [],
      ) as CategoryType[];

      return !category
        ? undefined
        : {
            ...category,
            childCategories: buildCategoryTree(children, categoriesMap),
          };
    }),
  );
}

const computeCategoriesTreeBreadcrumb = (
  prevBreadcrumbs: Record<string, Breadcrumb>,
  categoryTree: CategoryType[] | null,
  pathname: string | undefined,
  parentIds: string[],
) => {
  categoryTree?.forEach((category) => {
    prevBreadcrumbs[category.id] = {
      pageTitle: category.name,
      path: pathname,
      id: category.id,
      query: !parentIds.length
        ? {
            [CATEGORY_ID_QUERY_PARAM]: category.id,
          }
        : {
            [BREADCRUMB_QUERY_PARAM]: [...parentIds],
            [CATEGORY_ID_QUERY_PARAM]: category.id,
          },
    };

    if (category.childCategories?.length) {
      computeCategoriesTreeBreadcrumb(prevBreadcrumbs, category.childCategories, pathname, [
        ...parentIds,
        category.id,
      ]);
    }
  });

  return prevBreadcrumbs;
};

export default function useCategories(props?: UseCategoriesProps) {
  const { t } = useTranslation();
  const [{ fetching, data, error }] = useCategoriesQuery({
    variables: { ...props },
    requestPolicy: 'cache-and-network', // refresh content count after attaching content
  });
  const { pathname } = useLocation();
  const { setBreadcrumbHistory } = useBreadcrumbContext();
  const isHistorySet = React.useRef(false);

  const categoriesTree = React.useMemo(() => {
    if (
      !data?.rootCategory?.edges?.length ||
      !data?.categories?.edges?.length ||
      (props?.crossSubWorkspace && !props.subWorkspaceIds?.length)
    ) {
      return null;
    }

    const rootCategories = data.rootCategory.edges.map(({ node }) => node);

    const categoriesMap = buildCategoriesMap(data.categories.edges.map(({ node }) => node));

    return rootCategories.flatMap((rootCategory) => {
      const rootCategoryChildren = (rootCategory.childrenIds?.map((id) => categoriesMap?.[id]) ||
        []) as CategoryType[];

      return buildCategoryTree(rootCategoryChildren, categoriesMap);
    });
  }, [
    data?.categories?.edges,
    data?.rootCategory?.edges,
    props?.crossSubWorkspace,
    props?.subWorkspaceIds?.length,
  ]);

  const categoriesBreadcrumb = useMemo(() => {
    const defaultCategories: Record<string, Breadcrumb> = {
      [DefaultCategoryId.ALL_CONTENT]: {
        pageTitle: t('Discover:AllContent'),
        path: pathname,
        id: 'ALL_CONTENT',
        module: { type: 'ALL_CONTENT' },
        query: {
          [CATEGORY_ID_QUERY_PARAM]: DefaultCategoryId.ALL_CONTENT,
        },
      },
      [DefaultCategoryId.POPULAR_CONTENT]: {
        pageTitle: t('Discover:MostPopular'),
        path: pathname,
        id: 'POPULAR_CONTENT',
        module: { type: 'MOST_POPULAR' },
        query: {
          [CATEGORY_ID_QUERY_PARAM]: DefaultCategoryId.POPULAR_CONTENT,
        },
      },
      [DefaultCategoryId.NEW_CONTENT]: {
        pageTitle: t('Discover:NewlyAdded'),
        path: pathname,
        id: 'NEW_CONTENT',
        module: { type: 'NEWLY_ADDED' },
        query: {
          [CATEGORY_ID_QUERY_PARAM]: DefaultCategoryId.NEW_CONTENT,
        },
      },
    };

    if (fetching) {
      return defaultCategories;
    }

    return merge(
      {},
      defaultCategories,
      computeCategoriesTreeBreadcrumb({}, categoriesTree, pathname, []),
    ) as Record<string, Breadcrumb>;
  }, [categoriesTree, fetching, pathname, t]);

  //  todo @tapaskumar05 change later optimise to save only those needed
  useEffect(() => {
    if (isHistorySet.current) {
      return;
    }

    if (fetching) {
      return;
    }

    setBreadcrumbHistory((prev: Record<string, Breadcrumb>) => {
      return {
        ...prev,
        ...categoriesBreadcrumb,
      };
    });

    isHistorySet.current = true;
  }, [categoriesBreadcrumb, fetching, setBreadcrumbHistory]);

  return {
    fetching,
    rootCategory: (data?.rootCategory?.edges?.[0]?.node ?? null) as CategoryType | null,
    categoriesTree: categoriesTree || [],
    categoriesBreadcrumb,
    error,
  };
}
